import styled from 'particles/themes/styled'
import { Size } from '..'
import { Theme } from 'particles/themes'

interface DragContainerProps {
    isDragging?: boolean
    width?: string
    isCommentable: boolean
    isCommented: boolean
    isActive: boolean
    size: Size
}

export const DraggableBox = styled.div`
    display: flex;
    cursor: grab;
    width: fit-content;
    height: fit-content;
`

interface GetBackgroundColor {
    theme: Theme
    isActive: boolean
    isCommentable: boolean
    isCommented: boolean
}

const getBackgroundColor = ({ isActive, isCommentable, isCommented, theme }: GetBackgroundColor) => {
    if (!isCommentable) {
        return theme.colors.surface.primary
    }

    if (isActive) {
        return theme.colors.surface.warningActive
    }

    if (isCommented) {
        return theme.colors.surface.warningPastel
    }

    return theme.colors.surface.primary
}

export const DragContainer = styled.div<DragContainerProps>`
    position: relative;
    padding: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;
    box-shadow: ${({ theme, isDragging }) => (isDragging ? theme.shadows.level3 : 'none')};
    min-width: fit-content;
    width: ${({ width }) => (width ? width : '100%')};
    cursor: ${({ isCommentable }) => (isCommentable ? 'pointer' : 'default')};
    gap: ${({ theme }) => theme.spacing[100]};
    background-color: ${({ theme, isActive, isCommentable, isCommented }) =>
        getBackgroundColor({ isActive, isCommentable, isCommented, theme })};
    border-bottom: ${({ theme, isActive, isCommentable, isCommented }) =>
        isCommentable
            ? `1.5px solid ${isCommented || isActive ? theme.colors.border.warning : 'transparent'}`
            : 'none'};
    box-sizing: border-box;
    .title-area {
        background-color: transparent;
    }
    .action-button {
        margin-bottom: ${({ size }) => (size === 's' ? '4px' : '6px')};
    }
    .action-icon {
        margin-bottom: ${({ size }) => (size === 's' ? '8px' : '10px')};
    }
    &:hover {
        background-color: ${({ theme, isActive, isCommentable }) =>
            theme.colors.surface[
                isActive && isCommentable ? 'warningActive' : isCommentable ? 'neutralActionSubPastelHover' : 'primary'
            ]};
        border-bottom: ${({ theme, isCommentable, isActive }) =>
            isCommentable ? `1.5px solid ${theme.colors.border[isActive ? 'warning' : 'tertiary']}` : 'none'};
    }

    .textarea-container {
        display: flex;
        position: relative;
        width: 100%;

        .textarea-input-container {
            background-color: ${({ theme }) => theme.colors.surface.primary};
        }
    }

    .comment-icon {
        display: flex;
        height: fit-content;
        opacity: ${({ isActive, isCommented }) => (isActive || isCommented ? 1 : 0)};
    }

    &:hover .comment-icon,
    &:active .icon {
        opacity: 1;
    }
`

export interface TextListGroupContainerProps {
    width?: string
    maxWidth?: string
}

export const TextListGroupContainer = styled.div<TextListGroupContainerProps>`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[200]};
    width: ${({ width }) => (width ? width : '100%')};
    .list-item-field {
        display: flex;
        flex-direction: column;
        max-width: ${({ maxWidth }) => maxWidth || '600px'};
        gap: ${({ theme }) => theme.spacing[100]};
    }
    .width-fit-content {
        width: fit-content;
    }
    .draggable-group {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[200]};
    }
`

interface DoubleTextListGroupLabelProps {
    pl: number
    pr: number
    mainWidthRatio: string
    secondaryWidthRatio: string
    maxWidth?: string
}

export const DoubleTextListGroupLabel = styled.div<DoubleTextListGroupLabelProps>`
    box-sizing: border-box;
    width: 100%;
    max-width: ${({ maxWidth }) => maxWidth};
    padding: 0px ${({ pr }) => pr}px 0px ${({ pl }) => pl}px;
    display: flex;
    gap: 8px;

    .main-column-label {
        width: ${({ mainWidthRatio }) => mainWidthRatio};
        overflow: hidden;

        .label {
            overflow: hidden;
            text-wrap: nowrap;
            text-overflow: ellipsis;
            width: 100%;
        }
    }
    .secondary-column-label {
        width: ${({ secondaryWidthRatio }) => secondaryWidthRatio};
        overflow: hidden;

        .label {
            overflow: hidden;
            text-wrap: nowrap;
            text-overflow: ellipsis;
            width: 100%;
        }
    }
`

interface RichInfoListContainerProps {
    mainWidthRatio?: string
    secondaryWidthRatio?: string
    hasError?: boolean
    isDisabled?: boolean
}

export const RichInfoListContainer = styled.div<RichInfoListContainerProps>`
    display: flex;
    gap: 8px;
    flex: 1;
    align-items: center;

    .main-input {
        // 4 is gap between items / 2
        width: calc(${({ mainWidthRatio }) => mainWidthRatio || '75%'} - 4px);
    }

    .secondary-input {
        // 4 is gap between items / 2
        width: calc(${({ secondaryWidthRatio }) => secondaryWidthRatio || '25%'} - 4px);
    }

    .textfield-input-container {
        outline: 0;
        border: 1px solid
            ${({ theme, hasError, isDisabled }): string =>
                !isDisabled && hasError ? theme.colors.border.error : theme.colors.border.primary};
        background-color: ${({ theme }) => theme.colors.background.secondary};
    }
`
